import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash2, Calendar, Search, Filter, ChevronLeft, ChevronRight, Home, Paperclip, Download, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { financeiroService, ContaPagar, contasRecorrentesService, ContratoAberto } from '@/services/financeiroService';
import { useInterBankSyncPoller } from '@/hooks/useInterBankSyncPoller';
import { isDespesaOperacional, isDistribuicaoLucros } from '@/constants/categoriasFinanceiro';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format, parseISO, startOfMonth, addMonths, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getCurrentDateLocal, parseDateLocal, calcularDataVencimentoParcela, inferirModalidadeParcelamento, CATEGORIA_COMISSAO_CARREGADORES_PARCEIRO, CATEGORIA_COMISSAO_PARCEIRO_PAI, ordenarContasPagar, type ColunaOrdenacaoContaPagar, type SortDirection } from './contasPagarUtils';
import { ContasPagarDialogs, FormDataContaPagar } from './ContasPagarDialogs';
import { ContaDocumentacaoBadge } from './ContaDocumentacaoBadge';
import { useIsMobile } from '@/hooks/use-mobile';
import { useEmpresaFinanceira } from '@/contexts/EmpresaFinanceiraContext';
import { contaTemBoleto, contaTemNotaFiscal } from '@/utils/contasAnexos';

export function ContasPagar() {
  const isMobile = useIsMobile();
  const { empresaId, exigirEmpresa } = useEmpresaFinanceira();
  const [contas, setContas] = useState<ContaPagar[]>([]);
  const [contasRecorrentes, setContasRecorrentes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletarOpcao, setDeletarOpcao] = useState<'apenas' | 'todas' | 'futuras'>('apenas');
  const [editarEscopo, setEditarEscopo] = useState<'apenas' | 'futuras' | 'todas'>('apenas');
  const [editarEscopoDialogOpen, setEditarEscopoDialogOpen] = useState(false);
  const [dadosParaSalvar, setDadosParaSalvar] = useState<any>(null);
  const [alertaParcelasDialogOpen, setAlertaParcelasDialogOpen] = useState(false);
  const [alertaParcelasInfo, setAlertaParcelasInfo] = useState<{ tipo: 'reducao' | 'aumento'; quantidadeAtual: number; quantidadeNova: number } | null>(null);
  const [contaSelecionada, setContaSelecionada] = useState<ContaPagar | null>(null);
  const [filtroStatus, setFiltroStatus] = useState<string>('todos');
  const [busca, setBusca] = useState('');
  const [comprovanteDialogOpen, setComprovanteDialogOpen] = useState(false);
  const [comprovanteFile, setComprovanteFile] = useState<File | null>(null);
  const [uploadingComprovante, setUploadingComprovante] = useState(false);
  const [comprovanteFileForm, setComprovanteFileForm] = useState<File | null>(null);
  const [boletoFilesForm, setBoletoFilesForm] = useState<File[]>([]);
  const [notaFiscalFilesForm, setNotaFiscalFilesForm] = useState<File[]>([]);
  const [mesAtual, setMesAtual] = useState<string>(() => {
    // Inicializar com o mês mais recente que tem contas, ou o mês atual
    return format(startOfMonth(new Date()), 'yyyy-MM');
  });
  const [popoverAberto, setPopoverAberto] = useState(false);
  const [documentosDialogOpen, setDocumentosDialogOpen] = useState(false);
  const [contaParaDocumentos, setContaParaDocumentos] = useState<ContaPagar | null>(null);
  const [formData, setFormData] = useState<FormDataContaPagar>({
    descricao: '',
    valor: '',
    data_vencimento: '',
    categoria: '',
    fornecedor: '',
    contrato_id: '',
    contrato_tipo: '',
    parceiro_id: '',
    observacoes: '',
    tipo_pagamento: '',
    forma_pagamento: '',
    quantidade_parcelas: undefined,
    modalidade_parcelamento: 'mesmo_dia',
    // Campos de recorrência
    isRecorrente: false,
    frequencia: 'mensal',
    dia_semana: undefined,
    dia_mes: undefined,
    intervalo_personalizado: undefined,
    data_fim_recorrencia: '',
  });
  const [contratosAberto, setContratosAberto] = useState<ContratoAberto[]>([]);
  const [carregandoContratos, setCarregandoContratos] = useState(false);
  const [baixandoDocumentos, setBaixandoDocumentos] = useState(false);
  const [sortColumn, setSortColumn] = useState<ColunaOrdenacaoContaPagar>('data_vencimento');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const { refreshCounter: interSyncRefresh, syncStatus: interSyncStatus } = useInterBankSyncPoller();

  const uploadBoletosParaConta = async (contaId: string, files: File[]) => {
    for (const file of files) {
      const formDataUpload = new FormData();
      formDataUpload.append('boleto', file);
      const response = await fetch(`/api/contas-financeiro/pagar/${contaId}/boleto`, {
        method: 'POST',
        body: formDataUpload,
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao fazer upload do boleto');
      }
    }
  };

  const uploadNotasFiscaisParaConta = async (contaId: string, files: File[]) => {
    for (const file of files) {
      const formDataUpload = new FormData();
      formDataUpload.append('nota_fiscal', file);
      const response = await fetch(`/api/contas-financeiro/pagar/${contaId}/nota-fiscal`, {
        method: 'POST',
        body: formDataUpload,
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao fazer upload da nota fiscal');
      }
    }
  };

  useEffect(() => {
    console.log('🔄 useEffect: filtroStatus mudou para', filtroStatus);
    carregarContas();
  }, [filtroStatus, empresaId]);

  // Removido: Geração automática de contas recorrentes ao montar o componente
  // A geração agora acontece apenas quando uma nova conta recorrente é criada

  const carregarContas = async () => {
    try {
      console.log('🔄 Carregando contas a pagar...');
      setLoading(true);
      const filtros: any = {};
      if (filtroStatus !== 'todos') {
        filtros.status = filtroStatus;
      }
      if (empresaId) filtros.empresa_id = empresaId;
      const data = await financeiroService.listarContasPagar(filtros);
      console.log(`📊 Carregadas ${data.length} contas a pagar`);

      // Carregar contas recorrentes para fazer match e buscar frequência
      try {
        const recorrentes = await contasRecorrentesService.listarContasRecorrentesPagar(empresaId);
        setContasRecorrentes(recorrentes);

        // Enriquecer contas com frequência das contas recorrentes relacionadas
        const contasEnriquecidas = data.map(conta => {
          if (conta.tipo_pagamento === 'recorrente' && !conta.frequencia) {
            // Buscar conta recorrente relacionada pela descrição e valor
            // Para contas recorrentes, o valor não deve ser null, mas verificamos mesmo assim
            const contaRecorrente = recorrentes.find(
              r => r.descricao === conta.descricao &&
                   ((conta.valor !== null && conta.valor !== undefined && r.valor === conta.valor) ||
                    (conta.valor === null && r.valor === null))
            );
            if (contaRecorrente) {
              return { ...conta, frequencia: contaRecorrente.frequencia };
            }
          }
          return conta;
        });
        setContas(contasEnriquecidas);
      } catch (error) {
        // Se não conseguir carregar recorrentes, usar dados originais
        console.error('Erro ao carregar contas recorrentes:', error);
        setContas(data);
      }
    } catch (error) {
      console.error('Erro ao carregar contas a pagar:', error);
      toast.error('Erro ao carregar contas a pagar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (interSyncRefresh > 0) {
      carregarContas();
      const stats = interSyncStatus?.lastReconcileStats;
      const criadas = stats?.criadasPagar ?? 0;
      const conciliadas = stats?.conciliadasPagar ?? 0;
      if (criadas > 0 && conciliadas > 0) {
        toast.info(
          `Banco Inter: ${conciliadas} conta(s) conciliada(s) e ${criadas} nova(s) importada(s)`
        );
      } else if (conciliadas > 0) {
        toast.info(`${conciliadas} conta(s) a pagar conciliada(s) com o Banco Inter`);
      } else if (criadas > 0) {
        toast.info(`${criadas} nova(s) conta(s) a pagar importada(s) do Banco Inter`);
      }
    }
  }, [interSyncRefresh]);

  const carregarContratosAberto = async () => {
    try {
      setCarregandoContratos(true);
      const contratos = await financeiroService.listarContratosAberto();
      setContratosAberto(contratos);
    } catch (error) {
      console.error('Erro ao carregar contratos em aberto:', error);
      toast.error('Erro ao carregar contratos em aberto');
    } finally {
      setCarregandoContratos(false);
    }
  };

  const abrirDialog = async (conta?: ContaPagar) => {
    // Carregar contratos em aberto quando abrir o dialog
    await carregarContratosAberto();
    
    if (conta) {
      setContaSelecionada(conta);
      // Resetar escopo de edição ao abrir diálogo
      setEditarEscopo('apenas');
      // Modalidade: usar valor salvo ou inferir pelas datas quando null (transações antigas)
      let modalidade: 'mesmo_dia' | '28_dias' = (conta as ContaPagar).modalidade_parcelamento || 'mesmo_dia';
      if (conta.tipo_pagamento === 'parcelado' && conta.id_parcelamento && !(conta as ContaPagar).modalidade_parcelamento) {
        try {
          const parcelas = await financeiroService.listarParcelas(conta.id_parcelamento);
          if (parcelas.length >= 2) {
            modalidade = inferirModalidadeParcelamento(parcelas);
          }
        } catch (e) {
          console.warn('Não foi possível inferir modalidade das parcelas:', e);
        }
      }
      // Inferir tipo_pagamento quando null (registros antigos): parcelado se tem id_parcelamento, recorrente se tem recorrente_id, senão avista
      const tipoPagamentoInferido: 'avista' | 'parcelado' | 'recorrente' =
        conta.tipo_pagamento && ['avista', 'parcelado', 'recorrente'].includes(conta.tipo_pagamento)
          ? conta.tipo_pagamento as 'avista' | 'parcelado' | 'recorrente'
          : conta.recorrente_id
            ? 'recorrente'
            : conta.id_parcelamento
              ? 'parcelado'
              : 'avista';

      // A descrição não contém mais sufixo de parcela, usar diretamente
      const dadosIniciais = {
        descricao: conta.descricao,
        valor: conta.valor !== null && conta.valor !== undefined ? conta.valor.toString() : '',
        data_vencimento: conta.data_vencimento,
        categoria: conta.categoria,
        fornecedor: conta.fornecedor || '',
        contrato_id: conta.contrato_id || '',
        contrato_tipo: (conta as any).contrato_tipo ?? '',
        parceiro_id: conta.parceiro_id || '',
        observacoes: conta.observacoes || '',
        tipo_pagamento: tipoPagamentoInferido,
        forma_pagamento: conta.forma_pagamento || '',
        quantidade_parcelas: conta.quantidade_parcelas,
        modalidade_parcelamento: modalidade,
        isRecorrente: conta.tipo_pagamento === 'recorrente', // Marcar como recorrente se tipo de pagamento for recorrente
        frequencia: conta.frequencia || 'mensal',
        dia_semana: undefined as number | undefined,
        dia_mes: undefined as number | undefined,
        intervalo_personalizado: undefined as number | undefined,
        data_fim_recorrencia: '',
      };

      // Se for uma conta recorrente, buscar os dados da conta recorrente
      if (conta.tipo_pagamento === 'recorrente' && conta.recorrente_id) {
        try {
          const contaRecorrente = await contasRecorrentesService.obterContaRecorrentePagar(conta.recorrente_id);
          if (contaRecorrente) {
            dadosIniciais.frequencia = contaRecorrente.frequencia || dadosIniciais.frequencia;
            dadosIniciais.dia_semana = contaRecorrente.dia_semana;
            dadosIniciais.dia_mes = contaRecorrente.dia_mes;
            dadosIniciais.intervalo_personalizado = contaRecorrente.intervalo_personalizado;
            dadosIniciais.data_fim_recorrencia = contaRecorrente.data_fim || '';
          }
        } catch (error) {
          console.error('Erro ao carregar dados da conta recorrente:', error);
          // Continuar mesmo se houver erro ao buscar os dados da conta recorrente
        }
      }

      setFormData(dadosIniciais);
      setComprovanteFileForm(null); // Resetar arquivo ao editar
      setBoletoFilesForm([]);
      setNotaFiscalFilesForm([]);
    } else {
      setContaSelecionada(null);
      setFormData({
        descricao: '',
        valor: '',
        data_vencimento: '',
        categoria: '',
        fornecedor: '',
        contrato_id: '',
        contrato_tipo: '',
        parceiro_id: '',
        observacoes: '',
        tipo_pagamento: '',
        forma_pagamento: '',
        quantidade_parcelas: undefined,
        modalidade_parcelamento: 'mesmo_dia',
        isRecorrente: false,
        frequencia: 'mensal',
        dia_semana: undefined,
        dia_mes: undefined,
        intervalo_personalizado: undefined,
        data_fim_recorrencia: '',
      });
      setComprovanteFileForm(null); // Resetar arquivo ao criar nova
      setBoletoFilesForm([]);
      setNotaFiscalFilesForm([]);
    }
    setDialogOpen(true);
  };

  const salvarConta = async () => {
    try {
      if (!formData.descricao || !formData.data_vencimento || !formData.categoria) {
        toast.error('Preencha todos os campos obrigatórios');
        return;
      }
      if ((formData.categoria === CATEGORIA_COMISSAO_CARREGADORES_PARCEIRO || formData.categoria === CATEGORIA_COMISSAO_PARCEIRO_PAI) && !formData.parceiro_id?.trim()) {
        toast.error(formData.categoria === CATEGORIA_COMISSAO_PARCEIRO_PAI ? 'Selecione o parceiro pai para comissão de carregadores da rede' : 'Selecione o parceiro para comissão de carregadores');
        return;
      }

      // Valor é opcional, mas se informado deve ser válido
      let valor: number | null = null;
      if (formData.valor && formData.valor.trim() !== '') {
        const valorParsed = parseFloat(formData.valor);
        if (isNaN(valorParsed) || valorParsed <= 0) {
          toast.error('Valor inválido');
          return;
        }
        valor = valorParsed;
      }

      // Validação obrigatória para tipo de pagamento parcelado
      if (formData.tipo_pagamento === 'parcelado') {
        if (!formData.quantidade_parcelas || formData.quantidade_parcelas <= 0) {
          toast.error('Informe a quantidade de parcelas');
          return;
        }
      }

      // Validação obrigatória para tipo de pagamento recorrente
      if (formData.tipo_pagamento === 'recorrente') {
        if (!formData.frequencia) {
          toast.error('Selecione a frequência da recorrência');
          return;
        }
        if (formData.frequencia === 'semanal' && formData.dia_semana === undefined) {
          toast.error('Selecione o dia da semana para frequência semanal');
          return;
        }
        if ((formData.frequencia === 'quinzenal' || formData.frequencia === 'mensal' || formData.frequencia === 'trimestral' || formData.frequencia === 'semestral' || formData.frequencia === 'anual') && formData.dia_mes === undefined) {
          toast.error('Selecione o dia do mês para esta frequência');
          return;
        }
      }

      const dataVencimento = parseDateLocal(formData.data_vencimento);
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      
      let status: 'pendente' | 'pago' | 'vencido' = 'pendente';
      if (dataVencimento < hoje) {
        status = 'vencido';
      }
      // Preservar estado de pagamento ao editar - se estava pago, deve continuar pago
      if (contaSelecionada?.status === 'pago') {
        status = 'pago';
      }

      let contaId: string;
      if (contaSelecionada) {
        // Edição de conta existente
        if (contaSelecionada.tipo_pagamento === 'parcelado' && contaSelecionada.id_parcelamento && contaSelecionada.numero_parcela) {
          // Edição de parcela
          const quantidadeParcelasAtual = contaSelecionada.quantidade_parcelas || 0;
          const novaQuantidadeParcelas = formData.quantidade_parcelas || quantidadeParcelasAtual;
          
          // Verificar se há alterações além de quantidade_parcelas
          const descricaoMudou = formData.descricao !== contaSelecionada.descricao;
          const valorMudou = valor !== contaSelecionada.valor;
          const categoriaMudou = formData.categoria !== contaSelecionada.categoria;
          const fornecedorMudou = (formData.fornecedor || '') !== (contaSelecionada.fornecedor || '');
          const contratoMudou = (formData.contrato_id || '') !== (contaSelecionada.contrato_id || '');
          const observacoesMudaram = (formData.observacoes || '') !== (contaSelecionada.observacoes || '');
          const formaPagamentoMudou = (formData.forma_pagamento || '') !== (contaSelecionada.forma_pagamento || '');
          const dataVencimentoMudou = formData.data_vencimento !== contaSelecionada.data_vencimento;
          const quantidadeParcelasMudou = novaQuantidadeParcelas !== quantidadeParcelasAtual;
          
          const haOutrasAlteracoes = descricaoMudou || valorMudou || categoriaMudou || fornecedorMudou || 
                                     contratoMudou || observacoesMudaram || formaPagamentoMudou || dataVencimentoMudou;
          
          // Verificar se há arquivos para upload
          const haArquivosParaUpload = Boolean(comprovanteFileForm || boletoFilesForm.length || notaFiscalFilesForm.length);
          
          // Se apenas quantidade_parcelas mudou e não há arquivos, aplicar diretamente
          if (!haOutrasAlteracoes && !haArquivosParaUpload && quantidadeParcelasMudou) {
            await aplicarApenasQuantidadeParcelas(novaQuantidadeParcelas, quantidadeParcelasAtual);
            return;
          }
          
          // Se apenas quantidade_parcelas mudou mas há arquivos para upload
          if (!haOutrasAlteracoes && haArquivosParaUpload && quantidadeParcelasMudou) {
            await aplicarApenasQuantidadeParcelas(novaQuantidadeParcelas, quantidadeParcelasAtual);
            // Fazer upload dos arquivos após aplicar mudança de quantidade
            if (comprovanteFileForm) {
              try {
                const formDataUpload = new FormData();
                formDataUpload.append('comprovante', comprovanteFileForm);
                const response = await fetch(`/api/contas-financeiro/pagar/${contaSelecionada.id}/comprovante`, {
                  method: 'POST',
                  body: formDataUpload,
                });
                if (response.ok) {
                  toast.success('Comprovante enviado com sucesso');
                } else {
                  const error = await response.json();
                  throw new Error(error.error || 'Erro ao fazer upload do comprovante');
                }
              } catch (error: any) {
                console.error('Erro no upload do comprovante:', error);
                toast.error('Erro ao enviar comprovante: ' + (error.message || 'Erro desconhecido'));
              }
            }

            if (boletoFilesForm.length > 0) {
              try {
                await uploadBoletosParaConta(contaSelecionada.id, boletoFilesForm);
                toast.success(
                  boletoFilesForm.length > 1
                    ? `${boletoFilesForm.length} boletos enviados com sucesso`
                    : 'Boleto enviado com sucesso'
                );
              } catch (error: any) {
                console.error('Erro no upload do boleto:', error);
                toast.error('Erro ao enviar boleto: ' + (error.message || 'Erro desconhecido'));
              }
            }

            if (notaFiscalFilesForm.length > 0) {
              try {
                await uploadNotasFiscaisParaConta(contaSelecionada.id, notaFiscalFilesForm);
                toast.success(
                  notaFiscalFilesForm.length > 1
                    ? `${notaFiscalFilesForm.length} notas fiscais enviadas com sucesso`
                    : 'Nota fiscal enviada com sucesso'
                );
              } catch (error: any) {
                console.error('Erro no upload da nota fiscal:', error);
                toast.error('Erro ao enviar nota fiscal: ' + (error.message || 'Erro desconhecido'));
              }
            }

            setComprovanteFileForm(null);
            setBoletoFilesForm([]);
            setNotaFiscalFilesForm([]);
            // Não fechar o diálogo aqui - o alerta de quantidade de parcelas será mostrado e depois fechará
            carregarContas();
            return;
          }
          
          // Se há outras alterações (com ou sem mudança de quantidade), mostrar diálogo de escopo
          if (haOutrasAlteracoes) {
            // Preparar dados para atualização (preservar data_pagamento se estava pago)
            const dadosParaSalvarTemp: Partial<ContaPagar> = {
              descricao: formData.descricao,
              valor,
              categoria: formData.categoria,
              fornecedor: formData.fornecedor && formData.fornecedor.trim() !== '' ? formData.fornecedor : null,
              contrato_id: formData.contrato_id && formData.contrato_id.trim() !== '' ? formData.contrato_id : null,
              contrato_tipo: formData.contrato_tipo && formData.contrato_tipo.trim() !== '' ? formData.contrato_tipo : null,
              parceiro_id: formData.parceiro_id && formData.parceiro_id.trim() !== '' ? formData.parceiro_id : null,
              observacoes: formData.observacoes && formData.observacoes.trim() !== '' ? formData.observacoes : null,
              forma_pagamento: formData.forma_pagamento && formData.forma_pagamento.trim() !== '' ? formData.forma_pagamento : undefined,
              quantidade_parcelas: novaQuantidadeParcelas,
              data_vencimento: formData.data_vencimento,
              status,
              ...(status === 'pago' && contaSelecionada?.data_pagamento ? { data_pagamento: contaSelecionada.data_pagamento } : {}),
            };

            // Armazenar dados e abrir diálogo de escolha de escopo
            setDadosParaSalvar(dadosParaSalvarTemp);
            setEditarEscopo('apenas'); // Resetar para padrão
            setEditarEscopoDialogOpen(true);
            return; // Não continuar, aguardar escolha do usuário
          }
          
          // Se não há alterações mas há arquivos para upload, fazer upload diretamente
          if (!haOutrasAlteracoes && haArquivosParaUpload) {
            // Fazer upload dos arquivos diretamente para a conta selecionada
            if (comprovanteFileForm) {
              try {
                const formDataUpload = new FormData();
                formDataUpload.append('comprovante', comprovanteFileForm);
                const response = await fetch(`/api/contas-financeiro/pagar/${contaSelecionada.id}/comprovante`, {
                  method: 'POST',
                  body: formDataUpload,
                });
                if (response.ok) {
                  toast.success('Comprovante enviado com sucesso');
                } else {
                  const error = await response.json();
                  throw new Error(error.error || 'Erro ao fazer upload do comprovante');
                }
              } catch (error: any) {
                console.error('Erro no upload do comprovante:', error);
                toast.error('Erro ao enviar comprovante: ' + (error.message || 'Erro desconhecido'));
              }
            }

            if (boletoFilesForm.length > 0) {
              try {
                await uploadBoletosParaConta(contaSelecionada.id, boletoFilesForm);
                toast.success(
                  boletoFilesForm.length > 1
                    ? `${boletoFilesForm.length} boletos enviados com sucesso`
                    : 'Boleto enviado com sucesso'
                );
              } catch (error: any) {
                console.error('Erro no upload do boleto:', error);
                toast.error('Erro ao enviar boleto: ' + (error.message || 'Erro desconhecido'));
              }
            }

            if (notaFiscalFilesForm.length > 0) {
              try {
                await uploadNotasFiscaisParaConta(contaSelecionada.id, notaFiscalFilesForm);
                toast.success(
                  notaFiscalFilesForm.length > 1
                    ? `${notaFiscalFilesForm.length} notas fiscais enviadas com sucesso`
                    : 'Nota fiscal enviada com sucesso'
                );
              } catch (error: any) {
                console.error('Erro no upload da nota fiscal:', error);
                toast.error('Erro ao enviar nota fiscal: ' + (error.message || 'Erro desconhecido'));
              }
            }

            setDialogOpen(false);
            setComprovanteFileForm(null);
            setBoletoFilesForm([]);
            setNotaFiscalFilesForm([]);
            carregarContas();
            return;
          }
          
          // Se não há alterações e não há arquivos, apenas fechar o diálogo
          setDialogOpen(false);
          return;
        } else if (contaSelecionada.tipo_pagamento === 'recorrente' && contaSelecionada.recorrente_id) {
          // Edição de transação recorrente
          // Verificar se há alterações
          const descricaoMudou = formData.descricao !== contaSelecionada.descricao;
          const valorMudou = valor !== contaSelecionada.valor;
          const categoriaMudou = formData.categoria !== contaSelecionada.categoria;
          const fornecedorMudou = (formData.fornecedor || '') !== (contaSelecionada.fornecedor || '');
          const contratoMudou = (formData.contrato_id || '') !== (contaSelecionada.contrato_id || '');
          const observacoesMudaram = (formData.observacoes || '') !== (contaSelecionada.observacoes || '');
          const formaPagamentoMudou = (formData.forma_pagamento || '') !== (contaSelecionada.forma_pagamento || '');
          const dataVencimentoMudou = formData.data_vencimento !== contaSelecionada.data_vencimento;
          
          const haAlteracoes = descricaoMudou || valorMudou || categoriaMudou || fornecedorMudou || 
                              contratoMudou || observacoesMudaram || formaPagamentoMudou || dataVencimentoMudou;
          
          // Verificar se há arquivos para upload
          const haArquivosParaUpload = Boolean(comprovanteFileForm || boletoFilesForm.length || notaFiscalFilesForm.length);
          
          // Se há alterações, mostrar diálogo de escopo
          if (haAlteracoes) {
            // Preparar dados para atualização (preservar data_pagamento se estava pago)
            const dadosParaSalvarTemp: Partial<ContaPagar> = {
              descricao: formData.descricao,
              valor,
              categoria: formData.categoria,
              fornecedor: formData.fornecedor && formData.fornecedor.trim() !== '' ? formData.fornecedor : null,
              contrato_id: formData.contrato_id && formData.contrato_id.trim() !== '' ? formData.contrato_id : null,
              contrato_tipo: formData.contrato_tipo && formData.contrato_tipo.trim() !== '' ? formData.contrato_tipo : null,
              parceiro_id: formData.parceiro_id && formData.parceiro_id.trim() !== '' ? formData.parceiro_id : null,
              observacoes: formData.observacoes && formData.observacoes.trim() !== '' ? formData.observacoes : null,
              forma_pagamento: formData.forma_pagamento && formData.forma_pagamento.trim() !== '' ? formData.forma_pagamento : undefined,
              data_vencimento: formData.data_vencimento,
              status,
              ...(status === 'pago' && contaSelecionada?.data_pagamento ? { data_pagamento: contaSelecionada.data_pagamento } : {}),
            };

            // Armazenar dados e abrir diálogo de escolha de escopo
            setDadosParaSalvar(dadosParaSalvarTemp);
            setEditarEscopo('apenas'); // Resetar para padrão
            setEditarEscopoDialogOpen(true);
            return; // Não continuar, aguardar escolha do usuário
          }
          
          // Se não há alterações mas há arquivos para upload, fazer upload diretamente
          if (!haAlteracoes && haArquivosParaUpload) {
            // Fazer upload dos arquivos diretamente para a conta selecionada
            if (comprovanteFileForm) {
              try {
                const formDataUpload = new FormData();
                formDataUpload.append('comprovante', comprovanteFileForm);
                const response = await fetch(`/api/contas-financeiro/pagar/${contaSelecionada.id}/comprovante`, {
                  method: 'POST',
                  body: formDataUpload,
                });
                if (response.ok) {
                  toast.success('Comprovante enviado com sucesso');
                } else {
                  const error = await response.json();
                  throw new Error(error.error || 'Erro ao fazer upload do comprovante');
                }
              } catch (error: any) {
                console.error('Erro no upload do comprovante:', error);
                toast.error('Erro ao enviar comprovante: ' + (error.message || 'Erro desconhecido'));
              }
            }

            if (boletoFilesForm.length > 0) {
              try {
                await uploadBoletosParaConta(contaSelecionada.id, boletoFilesForm);
                toast.success(
                  boletoFilesForm.length > 1
                    ? `${boletoFilesForm.length} boletos enviados com sucesso`
                    : 'Boleto enviado com sucesso'
                );
              } catch (error: any) {
                console.error('Erro no upload do boleto:', error);
                toast.error('Erro ao enviar boleto: ' + (error.message || 'Erro desconhecido'));
              }
            }

            if (notaFiscalFilesForm.length > 0) {
              try {
                await uploadNotasFiscaisParaConta(contaSelecionada.id, notaFiscalFilesForm);
                toast.success(
                  notaFiscalFilesForm.length > 1
                    ? `${notaFiscalFilesForm.length} notas fiscais enviadas com sucesso`
                    : 'Nota fiscal enviada com sucesso'
                );
              } catch (error: any) {
                console.error('Erro no upload da nota fiscal:', error);
                toast.error('Erro ao enviar nota fiscal: ' + (error.message || 'Erro desconhecido'));
              }
            }

            setDialogOpen(false);
            setComprovanteFileForm(null);
            setBoletoFilesForm([]);
            setNotaFiscalFilesForm([]);
            carregarContas();
            return;
          }
          
          // Se não há alterações e não há arquivos, apenas fechar o diálogo
          setDialogOpen(false);
          return;
        } else {
          // Edição de conta não parcelada nem recorrente (preservar data_pagamento se estava pago)
          const dadosParaSalvar = {
            descricao: formData.descricao,
            valor,
            data_vencimento: formData.data_vencimento,
            categoria: formData.categoria,
            status,
            fornecedor: formData.fornecedor && formData.fornecedor.trim() !== '' ? formData.fornecedor : null,
            contrato_id: formData.contrato_id && formData.contrato_id.trim() !== '' ? formData.contrato_id : null,
            contrato_tipo: formData.contrato_tipo && formData.contrato_tipo.trim() !== '' ? formData.contrato_tipo : null,
            parceiro_id: formData.parceiro_id && formData.parceiro_id.trim() !== '' ? formData.parceiro_id : null,
            observacoes: formData.observacoes && formData.observacoes.trim() !== '' ? formData.observacoes : null,
            tipo_pagamento: formData.tipo_pagamento || undefined,
            forma_pagamento: formData.forma_pagamento && formData.forma_pagamento.trim() !== '' ? formData.forma_pagamento : undefined,
            quantidade_parcelas: formData.tipo_pagamento === 'parcelado' && formData.quantidade_parcelas ? formData.quantidade_parcelas : undefined,
            frequencia: formData.tipo_pagamento === 'recorrente' && formData.frequencia ? formData.frequencia : undefined,
            ...(status === 'pago' && contaSelecionada?.data_pagamento ? { data_pagamento: contaSelecionada.data_pagamento } : {}),
          };
          await financeiroService.atualizarContaPagar(contaSelecionada.id, dadosParaSalvar);
          contaId = contaSelecionada.id;
          toast.success('Conta atualizada com sucesso');
        }
      } else {
        const empresaDoLancamento = exigirEmpresa();
        if (!empresaDoLancamento) return;
        // Criação de nova conta
        if (formData.tipo_pagamento === 'parcelado' && formData.quantidade_parcelas) {
          // Criar múltiplas contas para pagamento parcelado
          // O valor informado é o valor de cada parcela, não o valor total
          if (valor === null) {
            toast.error('Valor é obrigatório para pagamento parcelado');
            return;
          }
          const idParcelamento = crypto.randomUUID();
          const quantidadeParcelas = formData.quantidade_parcelas;
          const valorParcela = valor; // O valor informado já é o valor de cada parcela
          const dataVencimentoInicial = parseDateLocal(formData.data_vencimento);
          const modalidade = (formData.modalidade_parcelamento || 'mesmo_dia') as 'mesmo_dia' | '28_dias';
          
          const contasCriadas: ContaPagar[] = [];
          
          for (let i = 1; i <= quantidadeParcelas; i++) {
            const dataVencimentoParcela = calcularDataVencimentoParcela(dataVencimentoInicial, i, 1, modalidade);
            const hoje = new Date();
            hoje.setHours(0, 0, 0, 0);
            
            let statusParcela: 'pendente' | 'pago' | 'vencido' = 'pendente';
            if (dataVencimentoParcela < hoje) {
              statusParcela = 'vencido';
            }
            
            const dadosParcela = {
              descricao: formData.descricao, // Descrição sem sufixo de parcela
              empresa_id: empresaDoLancamento,
              valor: valorParcela, // Valor da parcela (mesmo valor para todas)
              data_vencimento: format(dataVencimentoParcela, 'yyyy-MM-dd'),
              categoria: formData.categoria,
              status: statusParcela,
              fornecedor: formData.fornecedor && formData.fornecedor.trim() !== '' ? formData.fornecedor : null,
              contrato_id: formData.contrato_id && formData.contrato_id.trim() !== '' ? formData.contrato_id : null,
              contrato_tipo: formData.contrato_tipo && formData.contrato_tipo.trim() !== '' ? formData.contrato_tipo : null,
              parceiro_id: formData.parceiro_id && formData.parceiro_id.trim() !== '' ? formData.parceiro_id : null,
              observacoes: formData.observacoes && formData.observacoes.trim() !== '' ? formData.observacoes : null,
              tipo_pagamento: 'parcelado' as const,
              forma_pagamento: formData.forma_pagamento && formData.forma_pagamento.trim() !== '' ? formData.forma_pagamento : undefined,
              quantidade_parcelas: quantidadeParcelas,
              numero_parcela: i,
              id_parcelamento: idParcelamento,
              modalidade_parcelamento: modalidade,
            };
            
            const contaCriada = await financeiroService.criarContaPagar(dadosParcela);
            contasCriadas.push(contaCriada);
          }
          
          contaId = contasCriadas[0].id; // Usar o ID da primeira parcela para uploads
          const valorTotal = valorParcela * quantidadeParcelas;
          toast.success(`${quantidadeParcelas} parcelas de R$ ${valorParcela.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} criadas (Total: R$ ${valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })})`);
        } else {
          // Se for recorrente, criar a configuração de recorrência primeiro
          if (formData.tipo_pagamento === 'recorrente') {
            // Validações específicas por frequência
            if (formData.frequencia === 'semanal' && formData.dia_semana === undefined) {
              toast.error('Selecione o dia da semana para frequência semanal');
              return;
            }
            if ((formData.frequencia === 'quinzenal' || formData.frequencia === 'mensal' || formData.frequencia === 'trimestral' || formData.frequencia === 'semestral' || formData.frequencia === 'anual') && formData.dia_mes === undefined) {
              toast.error('Selecione o dia do mês para esta frequência');
              return;
            }

            try {
              // Criar a conta recorrente primeiro
              // Para contas recorrentes, o valor é opcional
              const contaRecorrenteCriada = await contasRecorrentesService.criarContaRecorrentePagar({
                descricao: formData.descricao,
                empresa_id: empresaDoLancamento,
                valor,
                categoria: formData.categoria,
                fornecedor: formData.fornecedor && formData.fornecedor.trim() !== '' ? formData.fornecedor : null,
                parceiro_id: formData.parceiro_id && formData.parceiro_id.trim() !== '' ? formData.parceiro_id : null,
                observacoes: formData.observacoes && formData.observacoes.trim() !== '' ? formData.observacoes : null,
                frequencia: formData.frequencia,
                dia_semana: formData.frequencia === 'semanal' ? formData.dia_semana : null,
                dia_mes: (formData.frequencia === 'quinzenal' || formData.frequencia === 'mensal' || formData.frequencia === 'trimestral' || formData.frequencia === 'semestral' || formData.frequencia === 'anual') ? formData.dia_mes : null,
                intervalo_personalizado: null,
                data_inicio: formData.data_vencimento,
                data_fim: formData.data_fim_recorrencia && formData.data_fim_recorrencia.trim() !== '' ? formData.data_fim_recorrencia : null,
                ativo: true,
              });
              console.log('Conta recorrente criada:', contaRecorrenteCriada);
              
              // Gerar contas apenas para esta recorrência específica (não todas as recorrências)
              try {
                console.log('🔄 Chamando endpoint específico para recorrente_id:', contaRecorrenteCriada.id);
                // Chamar API para gerar contas apenas para esta recorrência
                const response = await fetch('/api/contas-recorrentes/pagar/gerar-especifico', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
                  },
                  body: JSON.stringify({ recorrente_id: contaRecorrenteCriada.id }),
                });
                console.log('📡 Resposta do endpoint específico:', response.status);

                if (response.ok) {
                  const resultado = await response.json();
                  console.log('✅ Resultado da geração de contas específicas:', resultado);
                  if (resultado.contas_geradas > 0) {
                    toast.success(`${resultado.contas_geradas} contas geradas para os próximos 12 meses`);
                    // Usar o ID da primeira conta gerada para uploads
                    const contasGeradas = await financeiroService.listarContasPagar({});
                    const primeiraContaGerada = contasGeradas.find(
                      c => c.recorrente_id === contaRecorrenteCriada.id && c.data_vencimento === formData.data_vencimento
                    );
                    if (primeiraContaGerada) {
                      contaId = primeiraContaGerada.id;
                    }
                  } else {
                    console.warn('⚠️ Nenhuma conta foi gerada. Verifique se há contas recorrentes ativas.');
                    toast.warning('Conta recorrente criada, mas nenhuma conta foi gerada. Verifique a configuração.');
                  }
                } else {
                  const errorText = await response.text();
                  console.error('❌ Erro na resposta do endpoint específico:', response.status, errorText);
                  throw new Error(`Erro ao gerar contas específicas: ${response.status} - ${errorText}`);
                }
              } catch (error: any) {
                console.error('Erro ao gerar contas específicas:', error);
                toast.error('Conta recorrente criada, mas houve erro ao gerar as contas');
              }
              // Recarregar contas após gerar
              carregarContas();
            } catch (error: any) {
              console.error('Erro ao criar conta recorrente:', error);
              toast.error('Erro ao criar conta recorrente: ' + (error.message || 'Erro desconhecido'));
              return; // Não continuar se houver erro
            }
          } else {
            // Criar conta única (à vista)
            const dadosParaSalvar = {
              descricao: formData.descricao,
              empresa_id: empresaDoLancamento,
              valor,
              data_vencimento: formData.data_vencimento,
              categoria: formData.categoria,
              status,
              fornecedor: formData.fornecedor && formData.fornecedor.trim() !== '' ? formData.fornecedor : null,
              contrato_id: formData.contrato_id && formData.contrato_id.trim() !== '' ? formData.contrato_id : null,
              contrato_tipo: formData.contrato_tipo && formData.contrato_tipo.trim() !== '' ? formData.contrato_tipo : null,
              parceiro_id: formData.parceiro_id && formData.parceiro_id.trim() !== '' ? formData.parceiro_id : null,
              observacoes: formData.observacoes && formData.observacoes.trim() !== '' ? formData.observacoes : null,
              tipo_pagamento: formData.tipo_pagamento || undefined,
              forma_pagamento: formData.forma_pagamento && formData.forma_pagamento.trim() !== '' ? formData.forma_pagamento : undefined,
              quantidade_parcelas: undefined,
            };
            const contaCriada = await financeiroService.criarContaPagar(dadosParaSalvar);
            contaId = contaCriada.id;
            toast.success('Conta criada com sucesso');
          }
        }
      }

      // Fazer upload dos arquivos se houver arquivos selecionados
      if (comprovanteFileForm) {
        try {
          const formDataUpload = new FormData();
          formDataUpload.append('comprovante', comprovanteFileForm);

          const response = await fetch(`/api/contas-financeiro/pagar/${contaId}/comprovante`, {
            method: 'POST',
            body: formDataUpload,
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Erro ao fazer upload do comprovante');
          }
          toast.success('Comprovante enviado com sucesso');
        } catch (error: any) {
          console.error('Erro no upload do comprovante:', error);
          toast.error('Conta salva, mas erro ao enviar comprovante: ' + (error.message || 'Erro desconhecido'));
        }
      }

      if (boletoFilesForm.length > 0) {
        try {
          await uploadBoletosParaConta(contaId, boletoFilesForm);
          toast.success(
            boletoFilesForm.length > 1
              ? `${boletoFilesForm.length} boletos enviados com sucesso`
              : 'Boleto enviado com sucesso'
          );
        } catch (error: any) {
          console.error('Erro no upload do boleto:', error);
          toast.error('Conta salva, mas erro ao enviar boleto: ' + (error.message || 'Erro desconhecido'));
        }
      }

      if (notaFiscalFilesForm.length > 0) {
        try {
          await uploadNotasFiscaisParaConta(contaId, notaFiscalFilesForm);
          toast.success(
            notaFiscalFilesForm.length > 1
              ? `${notaFiscalFilesForm.length} notas fiscais enviadas com sucesso`
              : 'Nota fiscal enviada com sucesso'
          );
        } catch (error: any) {
          console.error('Erro no upload da nota fiscal:', error);
          toast.error('Conta salva, mas erro ao enviar nota fiscal: ' + (error.message || 'Erro desconhecido'));
        }
      }

      setDialogOpen(false);
      setComprovanteFileForm(null);
      setBoletoFilesForm([]);
      setNotaFiscalFilesForm([]);
      carregarContas();
    } catch (error: any) {
      console.error('Erro ao salvar conta:', error);
      const mensagemErro = error?.message || error?.error_description || 'Erro ao salvar conta';
      toast.error(`Erro ao salvar conta: ${mensagemErro}`);
    }
  };

  const marcarComoPago = async (conta: ContaPagar) => {
    try {
      await financeiroService.atualizarContaPagar(conta.id, {
        status: 'pago',
        data_pagamento: getCurrentDateLocal(),
      });
      toast.success('Conta marcada como paga');
      carregarContas();
    } catch (error) {
      console.error('Erro ao marcar como pago:', error);
      toast.error('Erro ao atualizar conta');
    }
  };

  const desmarcarComoPago = async (conta: ContaPagar) => {
    try {
      // Verificar se a conta está vencida para definir o status correto
      const dataVencimento = parseDateLocal(conta.data_vencimento);
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      
      let novoStatus: 'pendente' | 'vencido' = 'pendente';
      if (dataVencimento < hoje) {
        novoStatus = 'vencido';
      }

      await financeiroService.atualizarContaPagar(conta.id, {
        status: novoStatus,
        data_pagamento: null,
      });
      toast.success('Conta desmarcada como paga');
      carregarContas();
    } catch (error) {
      console.error('Erro ao desmarcar como pago:', error);
      toast.error('Erro ao atualizar conta');
    }
  };

  const deletarComprovante = async (contaId: string) => {
    try {
      const response = await fetch(`/api/contas-financeiro/pagar/${contaId}/comprovante`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao deletar comprovante');
      }

      toast.success('Comprovante deletado com sucesso');
      carregarContas();
      // Atualizar conta selecionada se estiver editando
      if (contaSelecionada?.id === contaId) {
        const contasAtualizadas = await financeiroService.listarContasPagar();
        const contaAtualizada = contasAtualizadas.find(c => c.id === contaId);
        if (contaAtualizada) {
          setContaSelecionada(contaAtualizada);
        }
      }
    } catch (error: any) {
      console.error('Erro ao deletar comprovante:', error);
      toast.error(error.message || 'Erro ao deletar comprovante');
    }
  };

  const deletarBoleto = async (contaId: string, url?: string) => {
    try {
      const qs = url ? `?url=${encodeURIComponent(url)}` : '';
      const response = await fetch(`/api/contas-financeiro/pagar/${contaId}/boleto${qs}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao deletar boleto');
      }

      toast.success('Boleto deletado com sucesso');
      carregarContas();
      if (contaSelecionada?.id === contaId) {
        const contasAtualizadas = await financeiroService.listarContasPagar();
        const contaAtualizada = contasAtualizadas.find(c => c.id === contaId);
        if (contaAtualizada) {
          setContaSelecionada(contaAtualizada);
        }
      }
    } catch (error: any) {
      console.error('Erro ao deletar boleto:', error);
      toast.error(error.message || 'Erro ao deletar boleto');
    }
  };

  const deletarNotaFiscal = async (contaId: string, url?: string) => {
    try {
      const qs = url ? `?url=${encodeURIComponent(url)}` : '';
      const response = await fetch(`/api/contas-financeiro/pagar/${contaId}/nota-fiscal${qs}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao deletar nota fiscal');
      }

      toast.success('Nota fiscal deletada com sucesso');
      carregarContas();
      if (contaSelecionada?.id === contaId) {
        const contasAtualizadas = await financeiroService.listarContasPagar();
        const contaAtualizada = contasAtualizadas.find(c => c.id === contaId);
        if (contaAtualizada) {
          setContaSelecionada(contaAtualizada);
        }
      }
    } catch (error: any) {
      console.error('Erro ao deletar nota fiscal:', error);
      toast.error(error.message || 'Erro ao deletar nota fiscal');
    }
  };

  const aplicarApenasQuantidadeParcelas = async (novaQuantidade: number, quantidadeAtual: number) => {
    if (!contaSelecionada || !contaSelecionada.id_parcelamento) return;
    
    try {
      if (novaQuantidade < quantidadeAtual) {
        // Reduzir parcelas - deletar as extras
        const { error: deleteError } = await supabase
          .from('contas_pagar')
          .delete()
          .eq('id_parcelamento', contaSelecionada.id_parcelamento)
          .gt('numero_parcela', novaQuantidade);

        if (deleteError) {
          console.error('Erro ao deletar parcelas extras:', deleteError);
          throw deleteError;
        }

        // Atualizar quantidade_parcelas em todas as parcelas restantes
        const { error: updateError } = await supabase
          .from('contas_pagar')
          .update({ quantidade_parcelas: novaQuantidade })
          .eq('id_parcelamento', contaSelecionada.id_parcelamento);

        if (updateError) {
          console.error('Erro ao atualizar quantidade de parcelas:', updateError);
          throw updateError;
        }

        // Mostrar alerta sobre redução
        setAlertaParcelasInfo({
          tipo: 'reducao',
          quantidadeAtual: quantidadeAtual,
          quantidadeNova: novaQuantidade,
        });
        setAlertaParcelasDialogOpen(true);
      } else if (novaQuantidade > quantidadeAtual) {
        // Aumentar parcelas - criar novas
        const parcelasExistentes = await financeiroService.listarParcelas(contaSelecionada.id_parcelamento);
        const ultimaParcela = parcelasExistentes.sort((a, b) => (b.numero_parcela || 0) - (a.numero_parcela || 0))[0];
        
        if (ultimaParcela) {
          const dataVencimentoUltima = parseDateLocal(ultimaParcela.data_vencimento);
          const modalidade = ultimaParcela.modalidade_parcelamento || 'mesmo_dia';
          
          for (let i = quantidadeAtual + 1; i <= novaQuantidade; i++) {
            const dataVencimentoParcela = calcularDataVencimentoParcela(dataVencimentoUltima, i, quantidadeAtual, modalidade);
            const hoje = new Date();
            hoje.setHours(0, 0, 0, 0);
            
            let statusParcela: 'pendente' | 'pago' | 'vencido' = 'pendente';
            if (dataVencimentoParcela < hoje) {
              statusParcela = 'vencido';
            }

            await financeiroService.criarContaPagar({
              descricao: ultimaParcela.descricao,
              empresa_id: ultimaParcela.empresa_id,
              valor: ultimaParcela.valor,
              data_vencimento: format(dataVencimentoParcela, 'yyyy-MM-dd'),
              categoria: ultimaParcela.categoria,
              status: statusParcela,
              fornecedor: ultimaParcela.fornecedor,
              contrato_id: ultimaParcela.contrato_id,
              contrato_tipo: (ultimaParcela as any).contrato_tipo ?? null,
              observacoes: ultimaParcela.observacoes,
              tipo_pagamento: 'parcelado',
              forma_pagamento: ultimaParcela.forma_pagamento,
              quantidade_parcelas: novaQuantidade,
              numero_parcela: i,
              id_parcelamento: contaSelecionada.id_parcelamento,
              modalidade_parcelamento: modalidade,
            });
          }

          // Atualizar quantidade_parcelas em todas as parcelas existentes
          const { error: updateError } = await supabase
            .from('contas_pagar')
            .update({ quantidade_parcelas: novaQuantidade })
            .eq('id_parcelamento', contaSelecionada.id_parcelamento);

          if (updateError) {
            console.error('Erro ao atualizar quantidade de parcelas:', updateError);
            throw updateError;
          }

          // Mostrar alerta sobre aumento
          setAlertaParcelasInfo({
            tipo: 'aumento',
            quantidadeAtual: quantidadeAtual,
            quantidadeNova: novaQuantidade,
          });
          setAlertaParcelasDialogOpen(true);
        }
      }

      // Não fechar o diálogo aqui - o alerta será mostrado e depois fechará
    } catch (error: any) {
      console.error('Erro ao aplicar alteração de quantidade de parcelas:', error);
      toast.error(`Erro ao aplicar alteração: ${error.message || 'Erro desconhecido'}`);
    }
  };

  const aplicarAlteracoesParcela = async () => {
    if (!contaSelecionada || !dadosParaSalvar) return;
    
    try {
      // Verificar se é parcela ou recorrente
      if (contaSelecionada.tipo_pagamento === 'parcelado' && contaSelecionada.id_parcelamento && contaSelecionada.numero_parcela) {
        // Lógica para parcelas
        const quantidadeParcelasAtual = contaSelecionada.quantidade_parcelas || 0;
        const novaQuantidadeParcelas = dadosParaSalvar.quantidade_parcelas || quantidadeParcelasAtual;
        
        // Preparar dados para atualização
        const dadosParaAtualizar: Partial<ContaPagar> = {
          descricao: dadosParaSalvar.descricao,
          valor: dadosParaSalvar.valor,
          categoria: dadosParaSalvar.categoria,
          fornecedor: dadosParaSalvar.fornecedor,
          contrato_id: dadosParaSalvar.contrato_id,
          contrato_tipo: dadosParaSalvar.contrato_tipo ?? undefined,
          parceiro_id: dadosParaSalvar.parceiro_id ?? undefined,
          observacoes: dadosParaSalvar.observacoes,
          forma_pagamento: dadosParaSalvar.forma_pagamento,
          quantidade_parcelas: novaQuantidadeParcelas,
        };

        // Se está editando apenas a parcela atual, atualizar também data_vencimento, status e data_pagamento
        if (editarEscopo === 'apenas') {
          dadosParaAtualizar.data_vencimento = dadosParaSalvar.data_vencimento;
          dadosParaAtualizar.status = dadosParaSalvar.status;
          if (dadosParaSalvar.status === 'pago' && dadosParaSalvar.data_pagamento) {
            dadosParaAtualizar.data_pagamento = dadosParaSalvar.data_pagamento;
          }
        }

        // Aplicar atualizações conforme escopo
        await financeiroService.atualizarParcelas(
          contaSelecionada.id_parcelamento!,
          dadosParaAtualizar,
          editarEscopo,
          contaSelecionada.numero_parcela
        );

      // Se quantidade de parcelas mudou, aplicar mudança e mostrar alerta após aplicar outras alterações
      if (novaQuantidadeParcelas !== quantidadeParcelasAtual) {
        // Aplicar mudança de quantidade de parcelas
        if (novaQuantidadeParcelas < quantidadeParcelasAtual) {
          // Reduzir parcelas - deletar as extras
          const { error: deleteError } = await supabase
            .from('contas_pagar')
            .delete()
            .eq('id_parcelamento', contaSelecionada.id_parcelamento)
            .gt('numero_parcela', novaQuantidadeParcelas);

          if (deleteError) {
            console.error('Erro ao deletar parcelas extras:', deleteError);
            throw deleteError;
          }

          // Atualizar quantidade_parcelas em todas as parcelas restantes
          const { error: updateError } = await supabase
            .from('contas_pagar')
            .update({ quantidade_parcelas: novaQuantidadeParcelas })
            .eq('id_parcelamento', contaSelecionada.id_parcelamento);

          if (updateError) {
            console.error('Erro ao atualizar quantidade de parcelas:', updateError);
            throw updateError;
          }

          // Mostrar alerta sobre redução após aplicar outras alterações
          setAlertaParcelasInfo({
            tipo: 'reducao',
            quantidadeAtual: quantidadeParcelasAtual,
            quantidadeNova: novaQuantidadeParcelas,
          });
        } else if (novaQuantidadeParcelas > quantidadeParcelasAtual && (editarEscopo === 'todas' || editarEscopo === 'futuras')) {
          // Aumentar parcelas - criar novas
          const parcelasExistentes = await financeiroService.listarParcelas(contaSelecionada.id_parcelamento!);
          const ultimaParcela = parcelasExistentes.sort((a, b) => (b.numero_parcela || 0) - (a.numero_parcela || 0))[0];
          
          if (ultimaParcela) {
            const dataVencimentoUltima = parseDateLocal(ultimaParcela.data_vencimento);
            const modalidade = ultimaParcela.modalidade_parcelamento || 'mesmo_dia';
            
            for (let i = quantidadeParcelasAtual + 1; i <= novaQuantidadeParcelas; i++) {
              const dataVencimentoParcela = calcularDataVencimentoParcela(dataVencimentoUltima, i, quantidadeParcelasAtual, modalidade);
              const hoje = new Date();
              hoje.setHours(0, 0, 0, 0);
              
              let statusParcela: 'pendente' | 'pago' | 'vencido' = 'pendente';
              if (dataVencimentoParcela < hoje) {
                statusParcela = 'vencido';
              }

              await financeiroService.criarContaPagar({
                descricao: dadosParaSalvar.descricao,
                empresa_id: ultimaParcela.empresa_id,
                valor: dadosParaSalvar.valor,
                data_vencimento: format(dataVencimentoParcela, 'yyyy-MM-dd'),
                categoria: dadosParaSalvar.categoria,
                status: statusParcela,
                fornecedor: dadosParaSalvar.fornecedor,
                contrato_id: dadosParaSalvar.contrato_id ?? undefined,
                contrato_tipo: dadosParaSalvar.contrato_tipo ?? undefined,
                parceiro_id: dadosParaSalvar.parceiro_id ?? undefined,
                observacoes: dadosParaSalvar.observacoes,
                tipo_pagamento: 'parcelado',
                forma_pagamento: dadosParaSalvar.forma_pagamento,
                quantidade_parcelas: novaQuantidadeParcelas,
                numero_parcela: i,
                id_parcelamento: contaSelecionada.id_parcelamento,
                modalidade_parcelamento: modalidade,
              });
            }

            // Atualizar quantidade_parcelas em todas as parcelas existentes
            const { error: updateError } = await supabase
              .from('contas_pagar')
              .update({ quantidade_parcelas: novaQuantidadeParcelas })
              .eq('id_parcelamento', contaSelecionada.id_parcelamento);

            if (updateError) {
              console.error('Erro ao atualizar quantidade de parcelas:', updateError);
              throw updateError;
            }

            // Mostrar alerta sobre aumento após aplicar outras alterações
            setAlertaParcelasInfo({
              tipo: 'aumento',
              quantidadeAtual: quantidadeParcelasAtual,
              quantidadeNova: novaQuantidadeParcelas,
            });
          }
        } else {
          // Apenas atualizar quantidade_parcelas sem criar/deletar (quando escopo é 'apenas')
          const { error: updateError } = await supabase
            .from('contas_pagar')
            .update({ quantidade_parcelas: novaQuantidadeParcelas })
            .eq('id_parcelamento', contaSelecionada.id_parcelamento);

          if (updateError) {
            console.error('Erro ao atualizar quantidade de parcelas:', updateError);
            throw updateError;
          }
        }
      }

      // Se escopo é 'todas' ou 'futuras' e data_vencimento mudou, atualizar datas das parcelas
      if ((editarEscopo === 'todas' || editarEscopo === 'futuras') && dadosParaSalvar.data_vencimento !== contaSelecionada.data_vencimento) {
        const parcelas = await financeiroService.listarParcelas(contaSelecionada.id_parcelamento!);
        const dataVencimentoInicial = parseDateLocal(dadosParaSalvar.data_vencimento);
        const modalidade = (parcelas[0]?.modalidade_parcelamento || contaSelecionada.modalidade_parcelamento) || 'mesmo_dia';
        
        for (const parcela of parcelas) {
          if (parcela.numero_parcela) {
            if (editarEscopo === 'todas' || (editarEscopo === 'futuras' && parcela.numero_parcela >= contaSelecionada.numero_parcela!)) {
              const parcelaReferencia = editarEscopo === 'todas' ? 1 : contaSelecionada.numero_parcela!;
              const novaDataVencimento = calcularDataVencimentoParcela(dataVencimentoInicial, parcela.numero_parcela, parcelaReferencia, modalidade);
              const hoje = new Date();
              hoje.setHours(0, 0, 0, 0);
              
              let novoStatus: 'pendente' | 'pago' | 'vencido' = parcela.status === 'deletada' ? 'pendente' : parcela.status;
              if (novaDataVencimento < hoje && (parcela.status === 'pendente' || parcela.status === 'deletada')) {
                novoStatus = 'vencido';
              }

              await financeiroService.atualizarContaPagar(parcela.id, {
                data_vencimento: format(novaDataVencimento, 'yyyy-MM-dd'),
                status: novoStatus,
              });
            }
          }
        }
      }
    } else if (contaSelecionada.tipo_pagamento === 'recorrente' && contaSelecionada.recorrente_id) {
        // Lógica para transações recorrentes
        // Preparar dados para atualização
        const dadosParaAtualizar: Partial<ContaPagar> = {
          descricao: dadosParaSalvar.descricao,
          valor: dadosParaSalvar.valor,
          categoria: dadosParaSalvar.categoria,
          fornecedor: dadosParaSalvar.fornecedor,
          parceiro_id: dadosParaSalvar.parceiro_id ?? undefined,
          observacoes: dadosParaSalvar.observacoes,
          forma_pagamento: dadosParaSalvar.forma_pagamento,
        };

        // Se está editando apenas a transação atual, atualizar também data_vencimento, status e data_pagamento
        if (editarEscopo === 'apenas') {
          dadosParaAtualizar.data_vencimento = dadosParaSalvar.data_vencimento;
          dadosParaAtualizar.status = dadosParaSalvar.status;
          if (dadosParaSalvar.status === 'pago' && dadosParaSalvar.data_pagamento) {
            dadosParaAtualizar.data_pagamento = dadosParaSalvar.data_pagamento;
          }
        }

        // Aplicar atualizações conforme escopo
        await financeiroService.atualizarRecorrentes(
          contaSelecionada.recorrente_id,
          dadosParaAtualizar,
          editarEscopo,
          contaSelecionada.data_vencimento
        );

        // Se escopo é 'todas' ou 'futuras' e data_vencimento mudou, atualizar datas das transações recorrentes
        if ((editarEscopo === 'todas' || editarEscopo === 'futuras') && dadosParaSalvar.data_vencimento !== contaSelecionada.data_vencimento) {
          const recorrentes = await financeiroService.listarRecorrentes(contaSelecionada.recorrente_id);
          const dataVencimentoInicial = parseDateLocal(dadosParaSalvar.data_vencimento);
          const dataVencimentoAtual = parseDateLocal(contaSelecionada.data_vencimento);
          
          for (const recorrente of recorrentes) {
            if (editarEscopo === 'todas' || (editarEscopo === 'futuras' && parseDateLocal(recorrente.data_vencimento) >= dataVencimentoAtual)) {
              // Para transações recorrentes, manter a diferença de dias entre as transações
              const diferencaDias = Math.floor((parseDateLocal(recorrente.data_vencimento).getTime() - dataVencimentoAtual.getTime()) / (1000 * 60 * 60 * 24));
              const novaDataVencimento = new Date(dataVencimentoInicial);
              novaDataVencimento.setDate(novaDataVencimento.getDate() + diferencaDias);
              const hoje = new Date();
              hoje.setHours(0, 0, 0, 0);
              
              let novoStatus: 'pendente' | 'pago' | 'vencido' = recorrente.status === 'deletada' ? 'pendente' : recorrente.status;
              if (novaDataVencimento < hoje && (recorrente.status === 'pendente' || recorrente.status === 'deletada')) {
                novoStatus = 'vencido';
              }

              await financeiroService.atualizarContaPagar(recorrente.id, {
                data_vencimento: format(novaDataVencimento, 'yyyy-MM-dd'),
                status: novoStatus,
              });
            }
          }
        }
      }

      // Fazer upload dos arquivos se houver
      const contaIdParaUpload = contaSelecionada.id;
      
      if (comprovanteFileForm) {
        try {
          const formDataUpload = new FormData();
          formDataUpload.append('comprovante', comprovanteFileForm);
          const response = await fetch(`/api/contas-financeiro/pagar/${contaIdParaUpload}/comprovante`, {
            method: 'POST',
            body: formDataUpload,
          });
          if (response.ok) {
            toast.success('Comprovante enviado com sucesso');
          }
        } catch (error: any) {
          console.error('Erro no upload do comprovante:', error);
        }
      }

      if (boletoFilesForm.length > 0) {
        try {
          await uploadBoletosParaConta(contaIdParaUpload, boletoFilesForm);
          toast.success(
            boletoFilesForm.length > 1
              ? `${boletoFilesForm.length} boletos enviados com sucesso`
              : 'Boleto enviado com sucesso'
          );
        } catch (error: any) {
          console.error('Erro no upload do boleto:', error);
        }
      }

      if (notaFiscalFilesForm.length > 0) {
        try {
          await uploadNotasFiscaisParaConta(contaIdParaUpload, notaFiscalFilesForm);
          toast.success(
            notaFiscalFilesForm.length > 1
              ? `${notaFiscalFilesForm.length} notas fiscais enviadas com sucesso`
              : 'Nota fiscal enviada com sucesso'
          );
        } catch (error: any) {
          console.error('Erro no upload da nota fiscal:', error);
        }
      }

      setEditarEscopoDialogOpen(false);
      setDadosParaSalvar(null);
      setEditarEscopo('apenas');
      
      // Se há alerta de parcelas para mostrar, mostrar o alerta primeiro
      if (alertaParcelasInfo) {
        setAlertaParcelasDialogOpen(true);
      } else {
        // Se não há alerta, fechar diálogo e recarregar
        setDialogOpen(false);
        setComprovanteFileForm(null);
        setBoletoFilesForm([]);
        setNotaFiscalFilesForm([]);
        
        const mensagemEscopo = editarEscopo === 'apenas' ? 'esta parcela' : editarEscopo === 'futuras' ? 'as parcelas futuras' : 'todas as parcelas';
        toast.success(`Alterações aplicadas em ${mensagemEscopo} com sucesso`);
        carregarContas();
      }
      // Se há alerta de parcelas, ele será mostrado e depois fechará o diálogo
    } catch (error: any) {
      console.error('Erro ao aplicar alterações:', error);
      toast.error(`Erro ao aplicar alterações: ${error.message || 'Erro desconhecido'}`);
    }
  };

  const deletarConta = async () => {
    if (!contaSelecionada) return;
    try {
      if (contaSelecionada.tipo_pagamento === 'parcelado' && contaSelecionada.id_parcelamento && contaSelecionada.numero_parcela) {
        if (deletarOpcao === 'todas') {
          // Deletar todas as parcelas do parcelamento
          await financeiroService.deletarTodasParcelas(contaSelecionada.id_parcelamento);
          toast.success('Todas as parcelas foram deletadas com sucesso');
        } else if (deletarOpcao === 'futuras') {
          // Deletar a parcela atual e as parcelas futuras
          await financeiroService.deletarParcelasFuturas(contaSelecionada.id_parcelamento, contaSelecionada.numero_parcela);
          const parcelasFuturas = contaSelecionada.quantidade_parcelas! - contaSelecionada.numero_parcela + 1;
          toast.success(`${parcelasFuturas} parcela(s) deletada(s) com sucesso`);
        } else {
          // Deletar apenas a parcela atual
          await financeiroService.deletarContaRecorrenteIndividual(
            contaSelecionada.id,
            contaSelecionada.recorrente_id!,
            contaSelecionada.data_vencimento
          );
          toast.success('Conta deletada com sucesso');
        }
      } else if (contaSelecionada.tipo_pagamento === 'recorrente' && contaSelecionada.recorrente_id) {
        if (deletarOpcao === 'todas') {
          // Deletar todas as transações recorrentes
          await financeiroService.deletarTodasRecorrentes(contaSelecionada.recorrente_id);
          toast.success('Todas as transações recorrentes foram deletadas com sucesso');
        } else if (deletarOpcao === 'futuras') {
          // Deletar a transação atual e as transações futuras
          await financeiroService.deletarRecorrentesFuturas(contaSelecionada.recorrente_id, contaSelecionada.data_vencimento);
          toast.success('Transação atual e transações futuras deletadas com sucesso');
        } else {
          // Deletar apenas a transação atual
          await financeiroService.deletarContaRecorrenteIndividual(
            contaSelecionada.id,
            contaSelecionada.recorrente_id!,
            contaSelecionada.data_vencimento
          );
          toast.success('Conta deletada com sucesso');
        }
      } else {
        // Deletar conta não parcelada nem recorrente
        await financeiroService.deletarContaPagar(contaSelecionada.id);
        toast.success('Conta deletada com sucesso');
      }
      setDeleteDialogOpen(false);
      setDeletarOpcao('apenas');
      setContaSelecionada(null);
      carregarContas();
    } catch (error) {
      console.error('Erro ao deletar conta:', error);
      toast.error('Erro ao deletar conta');
    }
  };

  const fazerUploadComprovante = async () => {
    if (!contaSelecionada || !comprovanteFile) {
      toast.error('Selecione um arquivo para upload');
      return;
    }

    try {
      setUploadingComprovante(true);

      const formData = new FormData();
      formData.append('comprovante', comprovanteFile);

      // Usar URL relativa - fetchWithAuth adiciona o token automaticamente
      const response = await fetch(`/api/contas-financeiro/pagar/${contaSelecionada.id}/comprovante`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao fazer upload');
      }

      toast.success('Comprovante enviado com sucesso');
      setComprovanteDialogOpen(false);
      setComprovanteFile(null);
      carregarContas();
    } catch (error) {
      console.error('Erro no upload:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao fazer upload');
    } finally {
      setUploadingComprovante(false);
    }
  };

  const contasFiltradas = contas.filter(conta => {
    const matchBusca = busca === '' || 
      conta.descricao.toLowerCase().includes(busca.toLowerCase()) ||
      conta.fornecedor?.toLowerCase().includes(busca.toLowerCase());
    return matchBusca;
  });

  // Agrupar contas por mês/ano baseado na data de vencimento
  const contasPorMes = contasFiltradas.reduce((acc, conta) => {
    const dataVencimento = parseDateLocal(conta.data_vencimento);
    const mesAno = format(startOfMonth(dataVencimento), 'yyyy-MM');
    const mesAnoLabel = format(startOfMonth(dataVencimento), "MMMM 'de' yyyy", { locale: ptBR });
    
    if (!acc[mesAno]) {
      acc[mesAno] = {
        label: mesAnoLabel,
        contas: [],
      };
    }
    acc[mesAno].contas.push(conta);
    return acc;
  }, {} as Record<string, { label: string; contas: ContaPagar[] }>);

  const handleSort = (column: ColunaOrdenacaoContaPagar) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Ordenar os meses do mais recente para o mais antigo
  const mesesOrdenados = Object.keys(contasPorMes).sort((a, b) => b.localeCompare(a));

  // Inicializar mesAtual com o mês mais recente que tem contas quando os dados carregam
  useEffect(() => {
    if (mesesOrdenados.length > 0) {
      const mesMaisRecente = mesesOrdenados[0];
      // Só atualizar se o mês atual não existir nos dados carregados
      if (!contasPorMes[mesAtual]) {
        setMesAtual(mesMaisRecente);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mesesOrdenados.join(',')]);

  // Funções de navegação entre meses
  const irParaMesAnterior = () => {
    const dataAtual = parseISO(`${mesAtual}-01`);
    const mesAnterior = format(subMonths(dataAtual, 1), 'yyyy-MM');
    setMesAtual(mesAnterior);
  };

  const irParaProximoMes = () => {
    const dataAtual = parseISO(`${mesAtual}-01`);
    const proximoMes = format(addMonths(dataAtual, 1), 'yyyy-MM');
    setMesAtual(proximoMes);
  };

  const voltarAoMesAtual = () => {
    const mesAtualFormatado = format(startOfMonth(new Date()), 'yyyy-MM');
    setMesAtual(mesAtualFormatado);
  };

  // Verificar se está no mês atual
  const estaNoMesAtual = () => {
    const mesAtualFormatado = format(startOfMonth(new Date()), 'yyyy-MM');
    return mesAtual === mesAtualFormatado;
  };

  // Verificar limites de navegação (permitir navegação livre, mas podemos desabilitar se necessário)
  // Por enquanto, permitimos navegação livre entre todos os meses

  // Obter o grupo do mês atual
  const grupoAtual = contasPorMes[mesAtual];
  const contasOrdenadas = grupoAtual
    ? ordenarContasPagar(grupoAtual.contas, sortColumn, sortDirection)
    : [];
  const mesAtualLabel = grupoAtual 
    ? format(parseISO(`${mesAtual}-01`), "MMMM/yyyy", { locale: ptBR })
    : format(parseISO(`${mesAtual}-01`), "MMMM/yyyy", { locale: ptBR });

  // Obter mês e ano atual para o seletor
  const dataAtual = parseISO(`${mesAtual}-01`);
  const mesSelecionado = dataAtual.getMonth();
  const anoSelecionado = dataAtual.getFullYear();

  // Gerar lista de anos (últimos 10 anos até próximos 2 anos)
  const anoAtual = new Date().getFullYear();
  const anosDisponiveis = Array.from({ length: 13 }, (_, i) => anoAtual - 10 + i);

  // Meses em português
  const meses = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  // Função para selecionar mês/ano
  const selecionarMesAno = (mes: number, ano: number) => {
    const novoMes = format(new Date(ano, mes, 1), 'yyyy-MM');
    setMesAtual(novoMes);
    setPopoverAberto(false);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pago':
        return <Badge variant="default" className="bg-success">Pago</Badge>;
      case 'vencido':
        return <Badge variant="destructive">Vencido</Badge>;
      default:
        return <Badge variant="secondary">Em Aberto</Badge>;
    }
  };

  // Verifica se uma conta possui arquivos anexados
  const temArquivosAnexados = (conta: ContaPagar): boolean => {
    return !!(
      conta.comprovante_url ||
      contaTemBoleto(conta) ||
      contaTemNotaFiscal(conta)
    );
  };

  // Abre o dialog de documentos
  const abrirDialogDocumentos = (conta: ContaPagar) => {
    setContaParaDocumentos(conta);
    setDocumentosDialogOpen(true);
  };

  // Função para fazer download de um arquivo
  const baixarArquivo = async (url: string, nomeArquivo: string) => {
    try {
      // Tenta fazer download direto da URL pública
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`Erro ao baixar arquivo: ${response.statusText}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = nomeArquivo || 'arquivo';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Download iniciado');
    } catch (error) {
      console.error('Erro ao baixar arquivo:', error);
      toast.error('Erro ao baixar arquivo. Tente novamente.');
    }
  };

  // Função para visualizar um arquivo em nova aba
  const visualizarArquivo = (url: string) => {
    window.open(url, '_blank');
  };

  // Verificar se há comprovantes, boletos ou notas fiscais no mês atual
  const temDocumentosParaBaixar = (): boolean => {
    if (!grupoAtual) return false;
    return grupoAtual.contas.some(conta =>
      conta.comprovante_url || contaTemBoleto(conta) || contaTemNotaFiscal(conta)
    );
  };

  // Função para baixar comprovantes, boletos e notas fiscais em um único ZIP
  const baixarDocumentosUnificadosZip = async () => {
    try {
      setBaixandoDocumentos(true);
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;

      if (!token) {
        toast.error('Erro de autenticação');
        return;
      }

      const response = await fetch(`/api/contas-financeiro/pagar/documentos-unificados-zip/${mesAtual}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao baixar documentos');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `documentos_${mesAtual}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Download de documentos iniciado');
    } catch (error: any) {
      console.error('Erro ao baixar documentos:', error);
      toast.error(error.message || 'Erro ao baixar documentos');
    } finally {
      setBaixandoDocumentos(false);
    }
  };

  return (
    <div className="space-y-4 min-[990px]:space-y-6">
      <div className="flex flex-col min-[990px]:flex-row min-[990px]:items-center min-[990px]:justify-between gap-3">
        <div>
          <h2 className="text-xl min-[990px]:text-2xl font-bold">Contas a Pagar</h2>
          <p className="text-xs min-[990px]:text-sm text-muted-foreground">Gerencie suas despesas e obrigações</p>
        </div>
        <Button 
          onClick={() => abrirDialog()} 
          className="w-full min-[990px]:w-auto gap-2 h-8 min-[990px]:h-10 text-xs min-[990px]:text-sm"
        >
          <Plus className="h-3 w-3 min-[990px]:h-4 min-[990px]:w-4" />
          {isMobile ? 'Nova' : 'Nova Transação'}
        </Button>
      </div>

      <Card>
        <CardHeader className="p-3 min-[990px]:p-6">
          <div className="flex flex-col min-[990px]:flex-row gap-3">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={isMobile ? "Buscar..." : "Buscar por descrição ou beneficiário..."}
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  className="pl-10 w-full h-8 min-[990px]:h-10 text-xs min-[990px]:text-sm"
                />
              </div>
            </div>
            {isMobile ? (
              <Drawer>
                <DrawerTrigger asChild>
                  <Button variant="outline" className="w-full">
                    <Filter className="h-4 w-4 mr-2" />
                    Filtrar
                  </Button>
                </DrawerTrigger>
                <DrawerContent>
                  <DrawerHeader>
                    <DrawerTitle>Filtrar Contas</DrawerTitle>
                  </DrawerHeader>
                  <div className="p-4 space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Status</label>
                      <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          <SelectItem value="pendente">Em Aberto</SelectItem>
                          <SelectItem value="pago">Pagas</SelectItem>
                          <SelectItem value="vencido">Vencidas</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </DrawerContent>
              </Drawer>
            ) : (
              <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                <SelectTrigger className="w-full min-[990px]:w-[180px] h-8 min-[990px]:h-10 text-xs min-[990px]:text-sm">
                  <Filter className="h-3 w-3 min-[990px]:h-4 min-[990px]:w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="pendente">Em Aberto</SelectItem>
                  <SelectItem value="pago">Pagas</SelectItem>
                  <SelectItem value="vencido">Vencidas</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-3 min-[990px]:p-6">
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : contasFiltradas.length === 0 ? (
            <div className="text-center p-8 text-muted-foreground">
              Nenhuma conta encontrada
            </div>
          ) : mesesOrdenados.length === 0 ? (
            <div className="text-center p-8 text-muted-foreground">
              Nenhuma conta encontrada para os filtros selecionados
            </div>
          ) : (
            <div className="space-y-4">
              {/* Navegação entre meses */}
              <div className="space-y-3 border-b pb-4">
                <div className="flex items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={irParaMesAnterior}
                    className="h-9 w-9 min-[990px]:h-10 min-[990px]:w-10"
                  >
                    <ChevronLeft className="h-4 w-4 min-[990px]:h-5 min-[990px]:w-5" />
                  </Button>
                  
                  <Popover open={popoverAberto} onOpenChange={setPopoverAberto}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        className="text-center hover:bg-accent cursor-pointer group px-4 min-[990px]:px-8"
                      >
                        <div className="flex items-center gap-2">
                          <h3 className="text-base min-[990px]:text-xl font-semibold capitalize">
                            {mesAtualLabel}
                          </h3>
                          <Calendar className="h-3 w-3 min-[990px]:h-4 min-[990px]:w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-4" align="center">
                      <div className="space-y-4">
                        <div className="text-sm font-medium text-center">Selecione o Mês</div>
                        <div className="grid grid-cols-3 gap-2">
                          {meses.map((mes, index) => (
                            <Button
                              key={index}
                              variant={mesSelecionado === index ? "default" : "outline"}
                              size="sm"
                              onClick={() => selecionarMesAno(index, anoSelecionado)}
                              className="text-xs"
                            >
                              {mes.substring(0, 3)}
                            </Button>
                          ))}
                        </div>
                        <div className="border-t pt-4">
                          <div className="text-sm font-medium mb-2 text-center">Ano</div>
                          <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                            {anosDisponiveis.map((ano) => (
                              <Button
                                key={ano}
                                variant={anoSelecionado === ano ? "default" : "outline"}
                                size="sm"
                                onClick={() => selecionarMesAno(mesSelecionado, ano)}
                                className="text-xs"
                              >
                                {ano}
                              </Button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                  
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={irParaProximoMes}
                    className="h-9 w-9 min-[990px]:h-10 min-[990px]:w-10"
                  >
                    <ChevronRight className="h-4 w-4 min-[990px]:h-5 min-[990px]:w-5" />
                  </Button>
                  
                  {!estaNoMesAtual() && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={voltarAoMesAtual}
                          className="h-9 w-9 min-[990px]:h-10 min-[990px]:w-10"
                        >
                          <Home className="h-4 w-4 min-[990px]:h-5 min-[990px]:w-5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Voltar ao mês atual</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                
                {grupoAtual && grupoAtual.contas.length > 0 && (() => {
                  const contasMes = grupoAtual.contas;
                  const somar = (lista: ContaPagar[]) =>
                    lista.reduce((acc, c) => acc + (c.valor || 0), 0);
                  const totalMes = somar(contasMes);
                  const pagasMes = somar(
                    contasMes.filter((c) => c.status === 'pago' && isDespesaOperacional(c.categoria))
                  );
                  const distribuicaoLucrosMes = somar(
                    contasMes.filter((c) => isDistribuicaoLucros(c.categoria))
                  );
                  const emAbertoMes = somar(
                    contasMes.filter(
                      (c) =>
                        (c.status === 'pendente' || c.status === 'vencido') &&
                        isDespesaOperacional(c.categoria)
                    )
                  );

                  return (
                  <div className="grid grid-cols-2 gap-2 min-[990px]:flex min-[990px]:flex-wrap min-[990px]:items-center min-[990px]:justify-center min-[990px]:gap-4 text-xs min-[990px]:text-sm">
                    <div className="col-span-2 min-[990px]:col-span-1 p-3 bg-muted rounded-lg min-[990px]:bg-transparent min-[990px]:p-0">
                      <span className="text-muted-foreground">Total:</span>
                      <span className="font-semibold text-foreground ml-2">
                        R$ {totalMes.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="p-3 bg-success/10 rounded-lg min-[990px]:bg-transparent min-[990px]:p-0">
                      <span className="text-muted-foreground">Pagas:</span>
                      <span className="font-semibold text-success ml-2">
                        R$ {pagasMes.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="p-3 bg-amber-500/10 rounded-lg min-[990px]:bg-transparent min-[990px]:p-0">
                      <span className="text-muted-foreground">Distribuição de Lucros:</span>
                      <span className="font-semibold text-amber-700 dark:text-amber-400 ml-2">
                        R$ {distribuicaoLucrosMes.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="p-3 bg-destructive/10 rounded-lg min-[990px]:bg-transparent min-[990px]:p-0">
                      <span className="text-muted-foreground">Em Aberto:</span>
                      <span className="font-semibold text-destructive ml-2">
                        R$ {emAbertoMes.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                  );
                })()}
              </div>

              {/* Lista de contas - Cards no mobile, Tabela no desktop */}
              {grupoAtual && grupoAtual.contas.length > 0 ? (
                isMobile ? (
                  // VERSÃO MOBILE: Cards estilo app financeiro
                  <div className="space-y-3">
                    {contasOrdenadas.map((conta) => {
                      // Buscar frequência para contas recorrentes
                      let frequenciaFormatada = '';
                      if (conta.tipo_pagamento === 'recorrente') {
                        let frequencia = conta.frequencia;
                        if (!frequencia) {
                          const contaRecorrente = contasRecorrentes.find(
                            r => r.descricao === conta.descricao && 
                                 ((conta.valor !== null && conta.valor !== undefined && r.valor === conta.valor) || 
                                  (conta.valor === null && r.valor === null))
                          );
                          frequencia = contaRecorrente?.frequencia;
                        }
                        frequenciaFormatada = frequencia === 'diaria' ? 'Diária' :
                                             frequencia === 'semanal' ? 'Semanal' :
                                             frequencia === 'quinzenal' ? 'Quinzenal' :
                                             frequencia === 'mensal' ? 'Mensal' :
                                             frequencia === 'trimestral' ? 'Trimestral' :
                                             frequencia === 'semestral' ? 'Semestral' :
                                             frequencia === 'anual' ? 'Anual' :
                                             frequencia || '';
                      }

                      return (
                        <Card 
                          key={conta.id}
                          className="cursor-pointer hover:bg-accent transition-colors"
                          onClick={() => abrirDialog(conta)}
                        >
                          <CardContent className="p-4">
                            {/* Header do card */}
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-base truncate">
                                  {conta.descricao}
                                </h3>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {conta.categoria}
                                </p>
                              </div>
                              {/* Status badge */}
                              <div className="ml-2 flex-shrink-0">
                                {getStatusBadge(conta.status)}
                              </div>
                            </div>

                            {/* Valor em destaque */}
                            <div className="mb-3">
                              <p className="text-2xl font-bold">
                                {conta.valor !== null && conta.valor !== undefined
                                  ? `R$ ${conta.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                  : <span className="text-muted-foreground text-lg">A definir</span>
                                }
                              </p>
                            </div>

                            {/* Informações secundárias em grid */}
                            <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground mb-3">
                              <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                <span>{format(parseDateLocal(conta.data_vencimento), "dd/MM/yyyy")}</span>
                              </div>
                              {conta.fornecedor && (
                                <div className="truncate">
                                  {conta.fornecedor}
                                </div>
                              )}
                              {conta.tipo_pagamento && (
                                <div className="col-span-2">
                                  {conta.tipo_pagamento === 'parcelado' && conta.numero_parcela && (
                                    <Badge variant="outline" className="text-xs">
                                      Parcela {conta.numero_parcela}/{conta.quantidade_parcelas}
                                    </Badge>
                                  )}
                                  {conta.tipo_pagamento === 'recorrente' && frequenciaFormatada && (
                                    <Badge variant="outline" className="text-xs">
                                      Recorrente • {frequenciaFormatada}
                                    </Badge>
                                  )}
                                  {conta.tipo_pagamento === 'avista' && (
                                    <Badge variant="outline" className="text-xs">
                                      À Vista
                                    </Badge>
                                  )}
                                </div>
                              )}
                              {conta.forma_pagamento && (
                                <div className="col-span-2 text-xs">
                                  Forma: {conta.forma_pagamento}
                                </div>
                              )}
                            </div>

                            {/* Ações rápidas */}
                            <div className="flex items-center justify-between pt-3 border-t">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">Pago?</span>
                                <Switch
                                  checked={conta.status === 'pago'}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      marcarComoPago(conta);
                                    } else {
                                      desmarcarComoPago(conta);
                                    }
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <ContaDocumentacaoBadge
                                  conta={conta}
                                  tipoConta="pagar"
                                  onUpdated={() => carregarContas()}
                                />
                                {temArquivosAnexados(conta) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      abrirDialogDocumentos(conta);
                                    }}
                                  >
                                    <Paperclip className="h-4 w-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    abrirDialog(conta);
                                  }}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setContaSelecionada(conta);
                                    setDeleteDialogOpen(true);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                ) : (
                  // VERSÃO DESKTOP: Tabela (mantém como está)
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className={`cursor-pointer ${sortColumn === 'descricao' ? 'text-primary' : ''}`} onClick={() => handleSort('descricao')}>
                          Descrição
                          {sortColumn === 'descricao' ? (
                            sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                          ) : (
                            <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                          )}
                        </TableHead>
                        <TableHead className={`cursor-pointer ${sortColumn === 'categoria' ? 'text-primary' : ''}`} onClick={() => handleSort('categoria')}>
                          Categoria
                          {sortColumn === 'categoria' ? (
                            sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                          ) : (
                            <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                          )}
                        </TableHead>
                        <TableHead className={`cursor-pointer ${sortColumn === 'fornecedor' ? 'text-primary' : ''}`} onClick={() => handleSort('fornecedor')}>
                          Beneficiário
                          {sortColumn === 'fornecedor' ? (
                            sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                          ) : (
                            <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                          )}
                        </TableHead>
                        <TableHead className={`cursor-pointer ${sortColumn === 'data_vencimento' ? 'text-primary' : ''}`} onClick={() => handleSort('data_vencimento')}>
                          Vencimento
                          {sortColumn === 'data_vencimento' ? (
                            sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                          ) : (
                            <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                          )}
                        </TableHead>
                        <TableHead className={`cursor-pointer ${sortColumn === 'valor' ? 'text-primary' : ''}`} onClick={() => handleSort('valor')}>
                          Valor
                          {sortColumn === 'valor' ? (
                            sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                          ) : (
                            <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                          )}
                        </TableHead>
                        <TableHead className={`cursor-pointer ${sortColumn === 'tipo_pagamento' ? 'text-primary' : ''}`} onClick={() => handleSort('tipo_pagamento')}>
                          Tipo de Pagamento
                          {sortColumn === 'tipo_pagamento' ? (
                            sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                          ) : (
                            <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                          )}
                        </TableHead>
                        <TableHead className={`cursor-pointer ${sortColumn === 'forma_pagamento' ? 'text-primary' : ''}`} onClick={() => handleSort('forma_pagamento')}>
                          Forma de Pagamento
                          {sortColumn === 'forma_pagamento' ? (
                            sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                          ) : (
                            <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                          )}
                        </TableHead>
                        <TableHead className={`cursor-pointer ${sortColumn === 'status' ? 'text-primary' : ''}`} onClick={() => handleSort('status')}>
                          Pago?
                          {sortColumn === 'status' ? (
                            sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                          ) : (
                            <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                          )}
                        </TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contasOrdenadas.map((conta) => (
                        <TableRow key={conta.id}>
                          <TableCell className="font-medium">{conta.descricao}</TableCell>
                          <TableCell>{conta.categoria}</TableCell>
                          <TableCell>{conta.fornecedor || '-'}</TableCell>
                          <TableCell>
                            {format(parseDateLocal(conta.data_vencimento), "dd/MM/yyyy", { locale: ptBR })}
                          </TableCell>
                          <TableCell className="font-semibold">
                            {conta.valor !== null && conta.valor !== undefined 
                              ? `R$ ${conta.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                              : <span className="text-muted-foreground italic">A definir</span>}
                          </TableCell>
                          <TableCell>
                            {conta.tipo_pagamento === 'avista' ? (
                              'À Vista'
                            ) : conta.tipo_pagamento === 'parcelado' ? (
                              conta.numero_parcela && conta.quantidade_parcelas ? (
                                `Parcelado (${conta.numero_parcela}/${conta.quantidade_parcelas})`
                              ) : conta.quantidade_parcelas ? (
                                `Parcelado (${conta.quantidade_parcelas}x)`
                              ) : (
                                'Parcelado'
                              )
                            ) : conta.tipo_pagamento === 'recorrente' ? (
                              <div className="flex flex-col">
                                <span className="font-medium">Recorrente</span>
                                <span className="text-xs text-muted-foreground mt-0.5">
                                  {(() => {
                                    // Buscar frequência da conta ou da conta recorrente relacionada
                                    let frequencia = conta.frequencia;
                                    if (!frequencia) {
                                      const contaRecorrente = contasRecorrentes.find(
                                        r => r.descricao === conta.descricao && 
                                             ((conta.valor !== null && conta.valor !== undefined && r.valor === conta.valor) || 
                                              (conta.valor === null && r.valor === null))
                                      );
                                      frequencia = contaRecorrente?.frequencia;
                                    }
                                    
                                    return frequencia === 'diaria' ? 'Diária' :
                                           frequencia === 'semanal' ? 'Semanal' :
                                           frequencia === 'quinzenal' ? 'Quinzenal' :
                                           frequencia === 'mensal' ? 'Mensal' :
                                           frequencia === 'trimestral' ? 'Trimestral' :
                                           frequencia === 'semestral' ? 'Semestral' :
                                           frequencia === 'anual' ? 'Anual' :
                                           frequencia || '-';
                                  })()}
                                </span>
                              </div>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell>
                            {conta.forma_pagamento || '-'}
                          </TableCell>
                          <TableCell>
                            <Switch
                              checked={conta.status === 'pago'}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  marcarComoPago(conta);
                                } else {
                                  desmarcarComoPago(conta);
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <ContaDocumentacaoBadge
                                conta={conta}
                                tipoConta="pagar"
                                onUpdated={() => carregarContas()}
                              />
                              {temArquivosAnexados(conta) && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => abrirDialogDocumentos(conta)}
                                    >
                                      <Paperclip className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Ver documentos anexados</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => abrirDialog(conta)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setContaSelecionada(conta);
                                  setDeleteDialogOpen(true);
                                }}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )
              ) : (
                <div className="text-center p-8 text-muted-foreground">
                  Nenhuma conta encontrada para {mesAtualLabel}
                </div>
              )}

              {/* Botão de download unificado */}
              {grupoAtual && grupoAtual.contas.length > 0 && temDocumentosParaBaixar() && (
                <div className="flex justify-center mt-6 pt-6 border-t">
                  <Button
                    variant="outline"
                    onClick={baixarDocumentosUnificadosZip}
                    disabled={baixandoDocumentos}
                    className="w-full min-[990px]:w-auto gap-2 h-8 min-[990px]:h-10 text-xs min-[990px]:text-sm"
                  >
                    {baixandoDocumentos ? (
                      <div className="animate-spin rounded-full h-3 w-3 min-[990px]:h-4 min-[990px]:w-4 border-b-2 border-current" />
                    ) : (
                      <Download className="h-3 w-3 min-[990px]:h-4 min-[990px]:w-4" />
                    )}
                    {isMobile ? 'Documentos (ZIP)' : `Baixar Documentos (${mesAtualLabel})`}
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <ContasPagarDialogs
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        contaSelecionada={contaSelecionada}
        formData={formData}
        setFormData={setFormData}
        salvarConta={salvarConta}
        deletarComprovante={deletarComprovante}
        deletarBoleto={deletarBoleto}
        deletarNotaFiscal={deletarNotaFiscal}
        comprovanteFileForm={comprovanteFileForm}
        setComprovanteFileForm={setComprovanteFileForm}
        boletoFilesForm={boletoFilesForm}
        setBoletoFilesForm={setBoletoFilesForm}
        notaFiscalFilesForm={notaFiscalFilesForm}
        setNotaFiscalFilesForm={setNotaFiscalFilesForm}
        deleteDialogOpen={deleteDialogOpen}
        setDeleteDialogOpen={setDeleteDialogOpen}
        deletarOpcao={deletarOpcao}
        setDeletarOpcao={setDeletarOpcao}
        deletarConta={deletarConta}
        editarEscopoDialogOpen={editarEscopoDialogOpen}
        setEditarEscopoDialogOpen={setEditarEscopoDialogOpen}
        editarEscopo={editarEscopo}
        setEditarEscopo={setEditarEscopo}
        dadosParaSalvar={dadosParaSalvar}
        setDadosParaSalvar={setDadosParaSalvar}
        aplicarAlteracoesParcela={aplicarAlteracoesParcela}
        alertaParcelasDialogOpen={alertaParcelasDialogOpen}
        setAlertaParcelasDialogOpen={setAlertaParcelasDialogOpen}
        alertaParcelasInfo={alertaParcelasInfo}
        setAlertaParcelasInfo={setAlertaParcelasInfo}
        carregarContas={carregarContas}
        comprovanteDialogOpen={comprovanteDialogOpen}
        setComprovanteDialogOpen={setComprovanteDialogOpen}
        comprovanteFile={comprovanteFile}
        setComprovanteFile={setComprovanteFile}
        uploadingComprovante={uploadingComprovante}
        fazerUploadComprovante={fazerUploadComprovante}
        documentosDialogOpen={documentosDialogOpen}
        setDocumentosDialogOpen={setDocumentosDialogOpen}
        contaParaDocumentos={contaParaDocumentos}
        visualizarArquivo={visualizarArquivo}
        baixarArquivo={baixarArquivo}
        contratosAberto={contratosAberto}
        carregandoContratos={carregandoContratos}
      />
    </div>
  );
}
